import React, { useState, useEffect } from 'react';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { logLifecycleEvent } from 'modules/App/_actionLogger';
import CrashSimulator from './CrashSimulator';
import ActionHistoryViewer from './ActionHistoryViewer';
import BrandTestSwitcher from './BrandTestSwitcher';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';

// Production builds set REACT_APP_ENV_MODE=PROD. Everything else (local dev,
// QA, DEV_WITH_LOGGING) is treated as non-prod.
const IS_PROD_BUILD = process.env.REACT_APP_ENV_MODE === 'PROD';

// Runtime escape hatch for the debug tools below. Lets you flip them on from the
// browser console without a rebuild:
//   localStorage.setItem('cg-debug-tools', '1'); location.reload();   // show
//   localStorage.removeItem('cg-debug-tools'); location.reload();     // hide
// Deliberately ignored in PROD (see IS_PROD_BUILD) so crash-testing tooling can
// never be surfaced on the production app. localStorage access can throw in
// sandboxed iframes / privacy modes, so it's guarded.
function debugToolsFlag() {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem('cg-debug-tools') : null;
  } catch {
    return null;
  }
}

/**
 * ErrorTestingTools component provides tools for testing error handling
 * Only visible in development and QA environments
 */
const ErrorTestingTools = () => {
  const flag = debugToolsFlag();
  const isDev =
    flag !== '0' &&
    (process.env.NODE_ENV === 'development' ||
      process.env.REACT_APP_ENV_MODE === 'DEV_WITH_LOGGING' ||
      (!IS_PROD_BUILD && flag === '1'));
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Log when error testing tools are opened
  useEffect(() => {
    if (showAdvanced) {
      logLifecycleEvent('ErrorTestingTools', 'advanced tools opened');
    }
  }, [showAdvanced]);

  if (!isDev) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50 flex flex-col items-start gap-2">
      <ActionHistoryViewer />

      {showAdvanced && (
        <div className="max-h-[80vh] w-80 overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 shadow-xl">
          <BrandTestSwitcher />
          <CrashSimulator />
        </div>
      )}

      <CgRadixButton
        variant="ghost"
        size="sm"
        onClick={() => setShowAdvanced(!showAdvanced)}
        icon={showAdvanced ? faChevronDown : faChevronUp}
        className="border border-red-200 text-red-600 hover:bg-red-50"
      >
        {showAdvanced ? 'Hide' : 'Show'} Advanced Crash Testing
      </CgRadixButton>
    </div>
  );
};

export default ErrorTestingTools;
